import { useCallback, useState } from 'react';
import {
  closestCenter,
  CollisionDetection,
  defaultDropAnimationSideEffects,
  DragEndEvent,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  MeasuringConfiguration,
  MeasuringStrategy,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GridGroup, HandleDropRequestProps } from '../types';
import { recalculatePageIndexes } from '../utils';
import { ReorderBundlePagesPasteLocationType, ReorderBundlePagesPasteLocationTypeKeys } from '@/api';

export enum Position {
  Before = -1,
  After = 1,
}

interface UseGridDragAndDropProps {
  groups: GridGroup[];
  setGroups: React.Dispatch<React.SetStateAction<GridGroup[]>>;
  isDragAndDropDisabled?: boolean;
  selectedThumbnailPageIds: string[];
  onDropRequest?: (props: HandleDropRequestProps) => void;
}

export const measuring: MeasuringConfiguration = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

/**
 * `pointerWithin` gives the most accurate target while the pointer is over a
 * thumbnail, but returns nothing when the pointer sits in the gap between
 * columns, over a group header, or when the drag is driven by the keyboard
 * sensor, so fall back to the nearest thumbnail in those cases.
 */
export const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);

  return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
};

const getOrderSignature = (groups: GridGroup[]) =>
  groups.map((group) => `${group.id}:${group.items.map((item) => item.id).join(',')}`).join('|');

/**
 * Moves the whole dragged selection next to `overId`, dropping after it when the
 * user is dragging forwards and before it when dragging backwards, which is the
 * placement the sortable preview leads them to expect. Returns null when the
 * move would not change anything, so callers can leave the grid untouched.
 */
const moveItemsRelativeToTarget = (groups: GridGroup[], draggedIds: string[], overId: string): GridGroup[] | null => {
  if (draggedIds.includes(overId)) return null;

  const targetGroupIndex = groups.findIndex((group) => group.items.some((item) => item.id === overId));
  if (targetGroupIndex === -1) return null;

  const flatItems = groups.flatMap((group) => group.items);
  const overFlatIndex = flatItems.findIndex((item) => item.id === overId);
  const firstDraggedFlatIndex = flatItems.findIndex((item) => draggedIds.includes(item.id));
  if (overFlatIndex === -1 || firstDraggedFlatIndex === -1) return null;

  const draggedItems = flatItems.filter((item) => draggedIds.includes(item.id));
  const dropsAfterTarget = firstDraggedFlatIndex < overFlatIndex;

  const nextGroups = groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => !draggedIds.includes(item.id)),
  }));

  const targetItems = nextGroups[targetGroupIndex].items;
  const targetItemIndex = targetItems.findIndex((item) => item.id === overId);
  targetItems.splice(dropsAfterTarget ? targetItemIndex + 1 : targetItemIndex, 0, ...draggedItems);

  const reorderedGroups = nextGroups.filter((group) => group.items.length > 0);
  if (getOrderSignature(groups) === getOrderSignature(reorderedGroups)) return null;

  return recalculatePageIndexes(reorderedGroups);
};

/**
 * The backend places the moved pages relative to a stationary page, so the
 * anchor has to be a page that is not itself being moved. Prefer the page just
 * before the moved block, and fall back to the one just after it.
 */
const findDropAnchor = (group: GridGroup, draggedIds: string[]) => {
  const firstDraggedIndex = group.items.findIndex((item) => draggedIds.includes(item.id));
  if (firstDraggedIndex === -1) return null;

  for (let index = firstDraggedIndex - 1; index >= 0; index--) {
    if (!draggedIds.includes(group.items[index].id)) {
      return {
        anchorPageId: group.items[index].id,
        pasteLocation: ReorderBundlePagesPasteLocationType.AFTER as ReorderBundlePagesPasteLocationTypeKeys,
      };
    }
  }

  for (let index = firstDraggedIndex + 1; index < group.items.length; index++) {
    if (!draggedIds.includes(group.items[index].id)) {
      return {
        anchorPageId: group.items[index].id,
        pasteLocation: ReorderBundlePagesPasteLocationType.BEFORE as ReorderBundlePagesPasteLocationTypeKeys,
      };
    }
  }

  return null;
};

const dropAnimation: DropAnimation = {
  keyframes({ transform }) {
    return [
      { transform: CSS.Transform.toString(transform.initial) },
      {
        transform: CSS.Transform.toString({
          scaleX: 0.98,
          scaleY: 0.98,
          x: transform.final.x - 10,
          y: transform.final.y - 10,
        }),
      },
    ];
  },
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '30%',
        backgroundColor: 'var(--color-gray-200)',
      },
    },
  }),
};

export const useGridDragAndDrop = ({
  groups,
  setGroups,
  isDragAndDropDisabled = false,
  selectedThumbnailPageIds,
  onDropRequest,
}: UseGridDragAndDropProps) => {
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [groupsBeforeDrag, setGroupsBeforeDrag] = useState<GridGroup[] | null>(null);

  const activePageIndex = activePageId ? groups.flatMap((g) => g.items).findIndex((i) => i.id === activePageId) : -1;
  // Resolved against the pre-drag snapshot: the live preview moves the page
  // between groups, and the drop restrictions have to keep comparing against the
  // group the page started in.
  const activeGroup = activePageId
    ? (groupsBeforeDrag ?? groups).find((g) => g.items.some((i) => i.id === activePageId))
    : null;

  const getDraggedIds = useCallback(
    (activeId: string, orderedGroups: GridGroup[]) => {
      if (!selectedThumbnailPageIds.includes(activeId)) return [activeId];

      // Send the pages in the order they appear in the bundle, because the
      // backend inserts them in the order they arrive.
      return orderedGroups
        .flatMap((group) => group.items)
        .filter((item) => selectedThumbnailPageIds.includes(item.id))
        .map((item) => item.id);
    },
    [selectedThumbnailPageIds],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (isDragAndDropDisabled) {
        return;
      }
      setActivePageId(event.active.id as string);
      setGroupsBeforeDrag(groups.map((g) => ({ ...g, items: [...g.items] })));
    },
    [isDragAndDropDisabled, groups],
  );

  const handleDragCancel = useCallback(() => {
    if (groupsBeforeDrag) {
      setGroups(groupsBeforeDrag);
    }
    setActivePageId(null);
    setGroupsBeforeDrag(null);
  }, [groupsBeforeDrag, setGroups]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const snapshot = groupsBeforeDrag;

      setActivePageId(null);
      setGroupsBeforeDrag(null);

      if (!snapshot) return;

      if (isDragAndDropDisabled) {
        setGroups(snapshot);
        return;
      }

      const { over } = event;
      const draggedIds = getDraggedIds(event.active.id as string, snapshot);

      // The grid stays still during the drag, so the final arrangement is worked
      // out once here from the page under the pointer rather than from a live
      // preview.
      const movedGroups = over ? moveItemsRelativeToTarget(snapshot, draggedIds, over.id as string) : null;

      // Dropped onto itself or back in the same spot, so leave the grid untouched.
      if (!movedGroups) {
        setGroups(snapshot);
        return;
      }

      const targetGroup = movedGroups.find((g) => g.items.some((i) => i.id === draggedIds[0]));
      const anchor = targetGroup ? findDropAnchor(targetGroup, draggedIds) : null;

      // The anchor is a page nobody is moving, so its position in the pre-drag
      // order is what the backend still sees.
      const anchorIndexBeforeDrag = anchor
        ? (snapshot
            .find((g) => g.items.some((i) => i.id === anchor.anchorPageId))
            ?.items.findIndex((i) => i.id === anchor.anchorPageId) ?? -1)
        : -1;

      // Nothing the backend can be asked to persist, so put the grid back.
      if (!targetGroup || !anchor || anchorIndexBeforeDrag === -1) {
        setGroups(snapshot);
        return;
      }

      // Show the move straight away, then persist it.
      setGroups(movedGroups);

      onDropRequest?.({
        targetParentId: targetGroup.id,
        anchorPageId: anchor.anchorPageId,
        targetIndex: anchorIndexBeforeDrag,
        pageIds: draggedIds,
        pasteLocation: anchor.pasteLocation,
        rollbackGroups: snapshot,
      });
    },
    [groupsBeforeDrag, setGroups, onDropRequest, isDragAndDropDisabled, getDraggedIds],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    activePageId,
    activePageIndex,
    activeGroup,
    sensors,
    measuring,
    collisionDetection,
    dropAnimation,
  };
};
